import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // When the axios interceptor exhausts both the access token and the refresh
  // token it fires this event so we can clear auth state and let ProtectedRoute
  // redirect to /login — without creating a circular import between api.js and
  // this context.
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("eduhub:session-expired", onExpired);
    return () => window.removeEventListener("eduhub:session-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  // Registration is now two-step: this only creates a pending record and
  // sends a verification email — no account exists and no cookies are set
  // yet, so `user` is intentionally left untouched here. The caller (Register
  // page) branches on `data.pending` to show the "check your email" panel.
  // The account is only created once /auth/verify-email succeeds (see
  // VerifyEmail.jsx, which calls `refresh()` after that request).
  const register = async (name, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      return data;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, error, setError, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
};
